import { parseStackFrame, shouldSkipFrame } from "../lib/reflux/monitoring";

/**
 * Stack-string parsing for DevTools. Which frames count as "library" comes
 * from Reflux monitoring's skip list, and the short "File.jsx:12" label from
 * its frame parser, so both DevTools tabs agree on what the call site is.
 *
 * @typedef {Object} StackFrame
 * @prop {string} fn - function name, or "" for anonymous frames
 * @prop {string} location - "src/components/File.jsx:12:7" (origin and query string removed)
 * @prop {string|null} shortLocation - "File.jsx:12", as the Reflux tab shows it
 * @prop {boolean} library - dependency or Reflux-internal frame
 */

/**
 * Removes the origin and any query string from a frame's URL, keeping the line and column.
 * @param {string} location - e.g. "http://localhost:8080/src/A.jsx?t=123:10:5"
 * @returns {string} e.g. "src/A.jsx:10:5"
 */
const shortenLocation = (location) => {
  const match = location.match(/^(.*?):(\d+):(\d+)$/);
  if (!match) return location;
  const [, url, line, col] = match;
  let path = url.split("?")[0];
  try {
    path = new URL(path).pathname;
  } catch (e) {
    // Not an absolute URL; keep it as is.
  }
  return `${path.replace(/^\//, "")}:${line}:${col}`;
};

/**
 * Parses a V8 or Firefox/Safari stack string into frames. The "Error" header,
 * native frames with no source ("Array.forEach (<anonymous>)") and lines that
 * aren't frames are dropped.
 * @param {string} [stack]
 * @returns {StackFrame[]}
 */
export const parseStack = (stack) => {
  if (!stack) return [];
  return stack.split("\n").flatMap((raw) => {
    const line = raw.trim();
    // V8: "at fn (url:line:col)" or "at url:line:col"
    const v8 = line.match(/^at (?:(.+?) \((.+)\)|(.+))$/);
    // Firefox/Safari: "fn@url:line:col"
    const gecko = !v8 && line.match(/^(.*?)@(.+:\d+:\d+)$/);
    if (!v8 && !gecko) return [];
    const rawLocation = v8 ? (v8[2] ?? v8[3]) : gecko[2];
    if (!/:\d+:\d+$/.test(rawLocation)) return [];
    return [
      {
        fn: v8 ? (v8[1] ?? "") : gecko[1],
        location: shortenLocation(rawLocation),
        shortLocation: parseStackFrame(line),
        library: shouldSkipFrame(line),
      },
    ];
  });
};

/**
 * Groups consecutive library frames so the stack reads as app code with
 * "N library frames" in between.
 * @param {StackFrame[]} frames
 * @returns {Array<{kind: "frame", frame: StackFrame} | {kind: "library", count: number}>}
 */
export const collapseLibraryFrames = (frames) =>
  frames.reduce((rows, frame) => {
    if (!frame.library) {
      rows.push({ kind: "frame", frame });
      return rows;
    }
    const last = rows[rows.length - 1];
    if (last?.kind === "library") last.count += 1;
    else rows.push({ kind: "library", count: 1 });
    return rows;
  }, []);
